import { useEffect } from 'react';
import invariant from 'tiny-invariant';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useTableParams } from 'business/providers/table-params';
import { DefectReportHistoryFilterData } from 'business/quality-control/pages/defect-report-history';
import { useDefectReportHistoryFilters } from 'business/quality-control/pages/defect-report-history/providers/filters';
import { useGetDefectIndicatorData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-defect-indicator';
import { useDefectReportV1ControllerGetList } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { FilterDataSetter } from 'technical/filter-data';
import { SORT_ORDER } from 'technical/graphql/sort-order';

import { formatFilterParams } from './utils';

export const useGetListData = (
  setFilterData: FilterDataSetter<DefectReportHistoryFilterData>,
) => {
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site');

  const filters = useDefectReportHistoryFilters();
  const { hasIndicators } = useGetDefectIndicatorData(
    currentConstructionSiteId,
  );

  const headers = useRestApiHeaders(Module_Enum.QualityControl);

  const tableParams = useTableParams();

  const sortField = tableParams.tableParams.sortField;
  const sortOrder = tableParams.tableParams.sortOrder
    ? SORT_ORDER[tableParams.tableParams.sortOrder]
    : null;

  const { data, isLoading, error, refetch } =
    useDefectReportV1ControllerGetList({
      pathParams: {
        constructionSiteId: currentConstructionSiteId,
      },
      queryParams: {
        ...formatFilterParams(filters, { hasIndicators }),
        paginationPage: tableParams.tableParams.current - 1, // we want to start at index 0,
        paginationSize: tableParams.tableParams.pageSize,
        paginationSortField: sortField?.toString(),
        paginationSortDirection: sortOrder,
      },
      headers,
    });

  useEffect(() => {
    setFilterData({
      selectedDefectReports: data?.total ?? 0,
    });
  }, [data, filters, setFilterData]);

  return {
    data: data?.entries ?? [],
    total: data?.total ?? 0,
    isLoading,
    error,
    refetch,
  };
};
